import Decimal from 'decimal.js';
import { disposalPhysCompositionConstRows, disposalPhysCompositionRowNames } from '@/utils/options';

const PERCENT_TOTAL_CENTS = 10000; // 100.00% × 100 (소수 2자리)
const CENTS_FACTOR = 100;
const WEIGHT_DIGITS = 2;

/** 공사현장 고정 토사류 행 라벨 — 가연성/비가연성과 동일 depth */
export const CONST_SOIL_CATEGORY = '토사류 (2mm 이하)';
export const CONST_SOIL_GROUP = '토사류';
export const CONST_ITEM_GROUPS = ['가연성', '비가연성'];

/**
 * 무게(kg) 확정값 — 빈값은 유지, 숫자는 소수 2자리 고정(0 → "0.00").
 * 비숫자(텍스트)는 허용하지 않고 빈 문자열로 정규화한다.
 * @param {string|number|null|undefined} value
 * @param {number} [digits=2]
 * @returns {string}
 */
export function formatPhysWeight(value, digits = WEIGHT_DIGITS) {
	if (value == null || String(value).trim() === '') return '';
	const n = Number(String(value).trim());
	if (!Number.isFinite(n)) return '';
	return n.toFixed(digits);
}

/**
 * 무게 입력 중 허용 문자열만 남긴다(숫자·소수점, 소수 최대 digits자리).
 * 중간 입력('.', '0.', '12.')은 유지하고, 문자·복수 소수점 등은 제거한다.
 * @param {string|number|null|undefined} raw
 * @param {number} [digits=2]
 * @returns {string}
 */
export function sanitizePhysWeightInput(raw, digits = WEIGHT_DIGITS) {
	if (raw == null) return '';
	let s = String(raw).replace(/[^\d.]/g, '');
	const firstDot = s.indexOf('.');
	if (firstDot >= 0) {
		s =
			s.slice(0, firstDot + 1) +
			s.slice(firstDot + 1).replace(/\./g, '');
	}
	if (firstDot >= 0) {
		const [intPart, frac = ''] = s.split('.');
		s = `${intPart}.${frac.slice(0, digits)}`;
	}
	return s;
}

function isSoilCategory(category) {
	const c = String(category || '').replace(/\s+/g, '');
	return c === '토사류(2mm이하)' || c === '토사류';
}

function isSoilRow(row) {
	if (!row) return false;
	if (row.group === CONST_SOIL_GROUP) return true;
	return isSoilCategory(row.category);
}

/**
 * 공사현장(156) 행 구조 보장.
 * - 가연성/비가연성 동적 항목 유지
 * - 토사류 (2mm 이하) 1행 항상 존재(가연성·비가연성과 동일 depth)
 * - 가연성 합계 / 비가연성 합계 / 총계 는 UI·엑셀에서 계산 행으로 노출(저장 rows 에는 미포함)
 */
export function ensureConstPhysStructure(rows) {
	const list = Array.isArray(rows) ? rows : [];
	const combustible = [];
	const nonCombustible = [];
	let soil = null;

	list.forEach((row) => {
		if (!row) return;
		if (isSoilRow(row)) {
			if (!soil) {
				soil = {
					group: CONST_SOIL_GROUP,
					category: row.category || CONST_SOIL_CATEGORY,
					weight: row.weight ?? '',
					ratio: row.ratio ?? '',
					fixed: true,
				};
			} else if (soil.weight === '' || soil.weight == null) {
				soil.weight = row.weight ?? '';
				soil.ratio = row.ratio ?? soil.ratio;
			}
			return;
		}
		const g = row.group || '가연성';
		const next = {
			group: g === '비가연성' ? '비가연성' : '가연성',
			category: row.category || '',
			weight: row.weight ?? '',
			ratio: row.ratio ?? '',
		};
		if (next.group === '비가연성') nonCombustible.push(next);
		else combustible.push(next);
	});

	if (!soil) {
		soil = {
			group: CONST_SOIL_GROUP,
			category: CONST_SOIL_CATEGORY,
			weight: '',
			ratio: '',
			fixed: true,
		};
	} else {
		soil.category = soil.category || CONST_SOIL_CATEGORY;
		soil.group = CONST_SOIL_GROUP;
		soil.fixed = true;
	}

	return [...combustible, ...nonCombustible, soil].map((row) => ({
		...row,
		weight: formatPhysWeight(row.weight),
	}));
}

/**
 * 무게 비율(%)을 소수 2자리로 분배한다. (Hamilton / largest remainder)
 * 행별 반올림 오차를 보정해 합계가 반드시 100.00이 된다.
 *
 * @param {Array<string|number|null|undefined>} weights
 * @returns {Array<string>} '' 또는 '12.34' 형식
 */
export function distributePercentRatios(weights) {
	if (!Array.isArray(weights) || !weights.length) return [];

	// 무게 0(직접 입력) → 비율 '0.00', 빈값/숫자아님 → '' 로 구분.
	// 반환 null = 양수(아래 Hamilton 분배로 계산).
	const zeroOrBlank = (raw) => {
		if (raw == null || String(raw).trim() === '') return '';
		const w = Number(raw);
		if (!Number.isFinite(w)) return '';
		return w <= 0 ? '0.00' : null;
	};

	const total = weights.reduce((sum, w) => sum + (Number(w) || 0), 0);
	if (!total) return weights.map((raw) => zeroOrBlank(raw) || '');

	const entries = weights.map((raw, idx) => {
		const w = Number(raw);
		if (!Number.isFinite(w) || w <= 0) {
			return { idx, active: false, cents: 0, remainder: new Decimal(0) };
		}
		const exact = new Decimal(w).div(total).mul(100);
		const cents = exact.mul(CENTS_FACTOR).floor().toNumber();
		const floored = new Decimal(cents).div(CENTS_FACTOR);
		return {
			idx,
			active: true,
			cents,
			remainder: exact.minus(floored),
		};
	});

	const active = entries.filter(e => e.active);
	if (active.length === 1) {
		return weights.map(raw => {
			const w = Number(raw);
			if (Number.isFinite(w) && w > 0) return '100.00';
			return zeroOrBlank(raw) || '';
		});
	}

	const sumCents = active.reduce((sum, e) => sum + e.cents, 0);
	let diff = PERCENT_TOTAL_CENTS - sumCents;
	if (diff < 0) diff = 0;

	const sorted = [...active].sort((a, b) => {
		const cmp = b.remainder.comparedTo(a.remainder);
		return cmp !== 0 ? cmp : a.idx - b.idx;
	});

	const centsByIdx = new Map(entries.map(e => [e.idx, e.cents]));
	for (let i = 0; i < diff; i++) {
		const target = sorted[i % sorted.length];
		centsByIdx.set(target.idx, centsByIdx.get(target.idx) + 1);
	}

	return weights.map((raw, idx) => {
		const w = Number(raw);
		if (!Number.isFinite(w) || w <= 0) return zeroOrBlank(raw) || '';
		return new Decimal(centsByIdx.get(idx) || 0).div(CENTS_FACTOR).toFixed(2);
	});
}

/** 공사현장(156) 조성항목 참고 템플릿 — UI는 검색·선택(고정행 강제 아님) */
export function createConstPhysRows(templateRows = disposalPhysCompositionConstRows) {
	return templateRows.map(({ group, category }) => ({
		group,
		category,
		weight: '',
		ratio: '',
	}));
}

/**
 * 저장된 physComposition 을 공사현장 고정행 슬롯에 병합한다.
 * - 슬롯 순서·항목명은 템플릿 고정, 무게·비율만 저장값 반영
 * - 동적 추가(구버전) 저장분은 group+category 로 슬롯 매칭, 기타는 그룹 내 빈 슬롯 순
 */
export function mergeConstPhysRows(saved, templateRows = disposalPhysCompositionConstRows) {
	const rows = createConstPhysRows(templateRows);
	if (!Array.isArray(saved) || !saved.length) return rows;

	const used = new Set();
	saved.forEach(item => {
		const group = item?.group || '가연성';
		const category = String(item?.category || '').trim();
		let idx = rows.findIndex(
			(r, i) =>
				!used.has(i) &&
				r.group === group &&
				category &&
				r.category === category,
		);
		if (idx < 0) {
			idx = rows.findIndex((r, i) => !used.has(i) && r.group === group);
		}
		if (idx < 0) return;
		used.add(idx);
		rows[idx] = {
			...rows[idx],
			category: category || rows[idx].category,
			weight: item.weight ?? '',
			ratio: item.ratio ?? '',
		};
	});
	return rows;
}

/** 시료별 저장 형태인지: [{ sampleNo, rows: [...] }, ...] */
export function isPhysCompositionBySample(data) {
	return Array.isArray(data) && data.some((item) => item && Array.isArray(item.rows));
}

/** 레거시 평탄 배열(조성행만)인지 */
export function isLegacyPhysCompositionRows(data) {
	if (!Array.isArray(data) || !data.length) return false;
	if (isPhysCompositionBySample(data)) return false;
	return data.some(
		(item) =>
			item &&
			(Object.prototype.hasOwnProperty.call(item, 'category') ||
				Object.prototype.hasOwnProperty.call(item, 'weight') ||
				Object.prototype.hasOwnProperty.call(item, 'ratio')),
	);
}

/** 토사류 고정행을 제외한 동적 조성항목이 있는지 */
export function hasDynamicPhysItems(rows) {
	return (rows || []).some((r) => {
		if (!r || r.fixed || r.group === CONST_SOIL_GROUP || isSoilRow(r)) return false;
		return String(r.category || '').trim() !== '';
	});
}

/**
 * 조성항목 구조만 복제 (무게·비율 비움). 공사현장은 토사류 고정행 포함.
 * @param {object[]} sourceRows
 * @param {{ isGrouped?: boolean }} [opts]
 */
export function clonePhysItemStructure(sourceRows, { isGrouped = false } = {}) {
	const list = Array.isArray(sourceRows) ? sourceRows : [];
	if (!isGrouped) {
		return list.map((r) => ({
			category: r?.category || '',
			weight: '',
			ratio: '',
		}));
	}
	const items = list
		.filter((r) => r && !r.fixed && r.group !== CONST_SOIL_GROUP && !isSoilRow(r))
		.map((r) => ({
			group: r.group === '비가연성' ? '비가연성' : '가연성',
			category: r.category || '',
			weight: '',
			ratio: '',
		}));
	return ensureConstPhysStructure(items);
}

/**
 * targetRows 를 source 항목 구조로 교체.
 * 공사현장: 대상 시료의 토사류 무게는 유지.
 */
export function applyPhysItemStructureToRows(targetRows, sourceRows, { isGrouped = false } = {}) {
	if (!Array.isArray(targetRows)) return;
	const next = clonePhysItemStructure(sourceRows, { isGrouped });
	if (isGrouped) {
		const prevSoil = targetRows.find((r) => r?.fixed || r?.group === CONST_SOIL_GROUP || isSoilRow(r));
		const soil = next.find((r) => r?.fixed || r?.group === CONST_SOIL_GROUP || isSoilRow(r));
		if (soil && prevSoil) {
			soil.category = prevSoil.category || CONST_SOIL_CATEGORY;
			soil.weight = formatPhysWeight(prevSoil.weight);
			soil.ratio = prevSoil.ratio ?? '';
		}
	}
	targetRows.splice(0, targetRows.length, ...next);
}

/**
 * 구조 변경 시료 → 아직 수동 편집하지 않은 다른 시료에 항목 목록 시드.
 * (최초 작성 시 시료1 선택 → 시료2… 반영, 이후 시료별 수동 변경)
 */
export function seedPhysCompositionStructureToUntouched(blocks, sourceIndex, { isGrouped = false } = {}) {
	if (!Array.isArray(blocks) || sourceIndex < 0 || sourceIndex >= blocks.length) return;
	const source = blocks[sourceIndex];
	if (!source || !Array.isArray(source.rows)) return;
	blocks.forEach((block, i) => {
		if (i === sourceIndex || !block || !Array.isArray(block.rows)) return;
		if (block._physStructureTouched) return;
		applyPhysItemStructureToRows(block.rows, source.rows, { isGrouped });
	});
}

function emptyRowsForMode(isConst) {
	return isConst ? createConstPhysRows() : [];
}

function createDefaultPhysRows() {
	return disposalPhysCompositionRowNames.map((category) => ({
		category,
		weight: '',
		ratio: '',
	}));
}

/**
 * 시료 목록 기준으로 physComposition 을 시료별 블록 배열로 정규화한다.
 * - 신규: [{ sampleNo, sampleName, sampleId, rows }]
 * - 레거시 평탄 배열: 동일 조성행을 각 시료에 복사(기존 성적서 전시료 동일값 호환)
 */
export function normalizePhysCompositionBySample(
	saved,
	samples,
	{ isConst = false, ensureConstStructure = false, seedDefaultRows = false } = {},
) {
	const sampleList = (samples || [])
		.map((s) => ({
			sampleNo: String(s?.sampleNo ?? s?.no ?? '').trim(),
			sampleName: s?.sampleName || s?.name || '',
			sampleId: s?.sampleId || s?.id || '',
		}))
		.filter((s) => s.sampleNo);

	const byNo = new Map();
	if (isPhysCompositionBySample(saved)) {
		saved.forEach((block) => {
			const no = String(block?.sampleNo ?? '').trim();
			if (!no) return;
			byNo.set(no, {
				sampleNo: no,
				sampleName: block.sampleName || '',
				sampleId: block.sampleId || '',
				rows: Array.isArray(block.rows) ? block.rows : [],
			});
		});
	} else if (isLegacyPhysCompositionRows(saved)) {
		// 구버전: 프로젝트 단위 1표 → 각 시료에 동일 행 복제
		sampleList.forEach((s) => {
			byNo.set(s.sampleNo, {
				...s,
				rows: saved.map((row) => ({ ...row })),
			});
		});
	}

	if (!sampleList.length) {
		return [...byNo.values()];
	}

	return sampleList.map((s) => {
		const prev = byNo.get(s.sampleNo);
		let rows = prev?.rows;
		if (!Array.isArray(rows)) rows = emptyRowsForMode(isConst);
		if (seedDefaultRows && !prev && rows.length === 0) {
			rows = createDefaultPhysRows();
		}
		// isConst: 과거 고정슬롯 병합. ensureConstStructure: 토사류 고정행만 보장(동적 항목 유지)
		if (isConst) rows = mergeConstPhysRows(rows);
		if (ensureConstStructure) rows = ensureConstPhysStructure(rows);
		else {
			rows = rows.map((row) => ({
				...row,
				weight: formatPhysWeight(row?.weight),
			}));
		}
		const block = {
			sampleNo: s.sampleNo,
			sampleName: s.sampleName || prev?.sampleName || '',
			sampleId: s.sampleId || prev?.sampleId || '',
			rows,
		};
		// 기저장 조성항목이 있으면 수동 편집된 것으로 간주(자동 시드 대상 제외)
		if (prev?._physStructureTouched || hasDynamicPhysItems(rows)) {
			block._physStructureTouched = true;
		}
		return block;
	});
}

/**
 * 성적서/결과통보서용: 시료별 조성행을 (group|category) 키로 합집합 전개.
 * @returns {{ key, group, category, name, isSoilRow, values: Map<sampleNo, ratio> }[]}
 */
export function flattenPhysCompositionForReport(physComposition, sampleNos, { isConst = false } = {}) {
	const nos = (sampleNos || []).map((n) => String(n));
	let blocks = [];
	if (isPhysCompositionBySample(physComposition)) {
		blocks = physComposition;
	} else if (isLegacyPhysCompositionRows(physComposition)) {
		blocks = nos.map((sampleNo) => ({ sampleNo, rows: physComposition }));
	}

	const byNo = new Map(
		blocks.map((b) => [String(b.sampleNo), Array.isArray(b.rows) ? b.rows : []]),
	);
	const ordered = [];
	const meta = new Map();

	nos.forEach((no) => {
		let rows = byNo.get(String(no)) || [];
		if (isConst) rows = mergeConstPhysRows(rows);
		rows.forEach((row) => {
			const isSoilRow = !row.category || row.group === '토사류';
			const name = isSoilRow ? (row.category || row.group || '') : String(row.category || '');
			if (!name) return;
			const key = `${row.group || ''}||${name}`;
			if (!meta.has(key)) {
				meta.set(key, {
					key,
					group: row.group || null,
					category: row.category || '',
					name,
					isSoilRow,
					values: new Map(),
				});
				ordered.push(key);
			}
			const ratio = row.ratio;
			meta.get(key).values.set(
				String(no),
				ratio === '' || ratio == null ? '' : String(ratio),
			);
		});
	});

	return ordered.map((key) => meta.get(key));
}
